import React, { useEffect, useState } from "react";
import { callFunction } from "../api/callFunction";
import { variantURL, fallbackToOriginal } from "../utils/imageVariant";
import { money } from "./GuestAppearances";
import { channelsLabel, formatSentAt, kindLabel } from "./outreachFormat";

/**
 * Everything sent to one member from the Popular guests tab, newest first, with
 * the text that actually went out. The answer to "what did we already say to
 * this person?" before an operator writes them again.
 */
export function OutreachHistoryDialog({ guest, eventID, onClose }) {
  const [messages, setMessages] = useState(null); // null until loaded
  const [error, setError] = useState("");

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = await callFunction("listGuestOutreach", { userID: guest.userID }, { timeout: 60000 });
        if (!cancelled) setMessages(Array.isArray(data?.messages) ? data.messages : []);
      } catch (err) {
        console.error("listGuestOutreach failed:", err);
        if (!cancelled) {
          setError(err?.message || "Could not load their history.");
          setMessages([]);
        }
      }
    })();
    return () => { cancelled = true; };
  }, [guest.userID]);

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/50 sm:p-4" role="dialog" aria-modal="true">
      <div className="w-full sm:max-w-md max-h-[92dvh] overflow-y-auto rounded-t-2xl sm:rounded-lg bg-white dark:bg-gray-900 p-4 sm:p-5 shadow-xl">
        <div className="flex items-center gap-3 mb-4">
          {guest?.photoURL ? (
            <img
              src={variantURL(guest.photoURL, 200)}
              onError={fallbackToOriginal(guest.photoURL)}
              alt={guest.name || "Member"}
              className="w-12 h-12 rounded-full object-cover shrink-0"
            />
          ) : (
            <div className="w-12 h-12 rounded-full bg-gray-200 dark:bg-gray-700 flex items-center justify-center shrink-0">
              <span className="text-gray-500 dark:text-gray-400">{(guest?.name || "?").charAt(0).toUpperCase()}</span>
            </div>
          )}
          <div className="min-w-0">
            <div className="font-medium text-gray-900 dark:text-gray-100 truncate">
              {guest?.name || "Unnamed member"}
            </div>
            <div className="text-xs text-gray-500 dark:text-gray-400 truncate">
              Everything sent to them from this tab
            </div>
          </div>
        </div>

        {messages === null && (
          <p className="py-6 text-center text-sm text-gray-500 dark:text-gray-400">Loading...</p>
        )}
        {error && (
          <div className="mb-3 p-3 rounded-md bg-red-50 dark:bg-red-900/40 border border-red-200 dark:border-red-800 text-red-800 dark:text-red-300 text-sm">
            {error}
          </div>
        )}
        {messages !== null && !error && messages.length === 0 && (
          <p className="py-6 text-center text-sm text-gray-500 dark:text-gray-400">
            Nobody has messaged them from this tab yet.
          </p>
        )}

        <div className="flex flex-col gap-2">
          {(messages || []).map((m) => {
            const aboutThisEvent = Boolean(eventID) && m.eventID === eventID;
            const details = [
              kindLabel(m.kind),
              channelsLabel(m.channels, m.reached),
              m.byName ? `by ${m.byName}` : null,
            ].filter(Boolean);
            const about = [
              m.eventName ? (aboutThisEvent ? "This event" : m.eventName) : null,
              m.amountCents != null ? money(m.amountCents) : null,
              m.couponCode ? `code ${m.couponCode}` : null,
            ].filter(Boolean);
            return (
              <div
                key={m.id}
                className={`p-3 rounded-md border ${aboutThisEvent ? "border-primary" : "border-gray-200 dark:border-gray-700"} bg-gray-50 dark:bg-gray-800`}
              >
                <div className="flex items-baseline justify-between gap-2 text-xs text-gray-500 dark:text-gray-400">
                  <span>{formatSentAt(m.sentAt)}</span>
                  <span className={m.reached ? "" : "text-amber-700 dark:text-amber-400"}>{details.join(" · ")}</span>
                </div>
                {about.length > 0 && (
                  <div className="mt-0.5 text-xs text-gray-600 dark:text-gray-300">{about.join(" · ")}</div>
                )}
                {m.text && (
                  <p className="mt-2 text-sm text-gray-800 dark:text-gray-200 whitespace-pre-line break-words">{m.text}</p>
                )}
                {/* Sent before the log existed: the wording is rebuilt from the
                    same composer, not the text we captured. Say so rather than
                    passing a reconstruction off as the real message. */}
                {m.textApproximate && (
                  <p className="mt-1 text-xs italic text-gray-500 dark:text-gray-400">
                    {m.text ? "Reconstructed — sent before messages were logged." : "Sent before messages were logged; the wording was not kept."}
                  </p>
                )}
              </div>
            );
          })}
        </div>

        <div className="flex justify-end mt-4">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 rounded-md font-medium bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-gray-200 hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}

export default OutreachHistoryDialog;
